import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionResourcesService } from '../services/resources.service';
import type { ConstructionResourceAssignment, ResourceFilters, ConstructionDailyReport } from '../types/construction-resources.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useConstructionResources(companyId: string, initialFilters: ResourceFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ResourceFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['construction-resources', companyId, filters],
    queryFn: () => ConstructionResourcesService.getAssignments(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-resource-stats', companyId, filters.projectId],
    queryFn: () => ConstructionResourcesService.getResourceStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const assignMutation = useMutation({
    mutationFn: (data: Partial<ConstructionResourceAssignment>) => ConstructionResourcesService.assignResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-resources'] });
      toast.success('Phân bổ tài nguyên thành công');
    },
  });

  return {
    assignments: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    assignResource: assignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
  };
}

export function useConstructionDailyReports(companyId: string, projectId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['construction-daily-reports', companyId, projectId],
    queryFn: () => ConstructionResourcesService.getDailyReports(companyId, projectId),
    enabled: !!companyId,
  });

  return {
    reports: query.data || [],
    isLoading: query.isLoading,
  };
}
